import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { getUserAccessibleProjects, getRelatedPeopleForUser } from '../utils/userPermissions';
import { 
  Plus, 
  Users, 
  Briefcase, 
  Target, 
  TrendingUp, 
  CheckCircle, 
  Clock, 
  Sparkles,
  User
} from 'lucide-react';
import { api } from '../services/api';

const Dashboard = () => {
  const { isDarkMode } = useTheme();
  const { currentUser, isAdmin, openOnboarding } = useAuth();

  // Dynamic stats from server Neo4j database & localStorage data
  const [stats, setStats] = useState([
    { label: 'Active Projects', value: 0, icon: Briefcase, color: 'text-blue-400', bgColor: 'bg-blue-500/20' },
    { label: 'Team Members', value: 0, icon: Users, color: 'text-green-400', bgColor: 'bg-green-500/20' },
    { label: 'Skills Required', value: 0, icon: Target, color: 'text-purple-400', bgColor: 'bg-purple-500/20' },
    { label: 'Completion Rate', value: '0%', icon: TrendingUp, color: 'text-orange-400', bgColor: 'bg-orange-500/20' }
  ]);

  const [recentProjects, setRecentProjects] = useState([]);
  const [skillGaps, setSkillGaps] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);

  // Fetch fresh server data on mount
  useEffect(() => {
    api.syncAll().then(() => {
      window.dispatchEvent(new Event('storage'));
    });
  }, []);

  // Update stats & recent projects scoped to user's published projects
  useEffect(() => {
    const updateDashboard = () => {
      const allProjects = JSON.parse(localStorage.getItem('skillmesh_projects') || '[]');
      const rawPeople = JSON.parse(localStorage.getItem('skillmesh_people') || '[]');
      const allSkills = JSON.parse(localStorage.getItem('skillmesh_skills') || '[]');

      // Deduplicate people to avoid inflated member counts
      const seenEmails = new Set();
      const allPeople = [];
      rawPeople.forEach(p => {
        const em = (p.email || '').toLowerCase().trim();
        const key = em || String(p.id);
        if (key && !seenEmails.has(key)) {
          seenEmails.add(key);
          allPeople.push(p);
        }
      });
      if (allPeople.length !== rawPeople.length) {
        localStorage.setItem('skillmesh_people', JSON.stringify(allPeople));
      }

      // Scoped data based on user role
      const userProjects = getUserAccessibleProjects(allProjects, currentUser, isAdmin);
      const userPeople = getRelatedPeopleForUser(allPeople, userProjects, currentUser, isAdmin);

      const activeProjects = userProjects.filter(p => p.status !== 'Completed').length;
      const totalProjects = userProjects.length;
      const completionRate = totalProjects > 0 
        ? Math.round((userProjects.filter(p => p.status === 'Completed').length / totalProjects) * 100) 
        : 0;

      // Unique skills required by user's projects
      const uniqueSkills = new Set();
      userProjects.forEach(p => {
        if (Array.isArray(p.requiredSkills)) {
          p.requiredSkills.forEach(s => {
            const sName = typeof s === 'string' ? s : s?.name;
            if (sName) uniqueSkills.add(sName.toLowerCase());
          });
        }
      });

      setStats([
        { 
          label: isAdmin ? 'Active Projects' : 'My Active Projects', 
          value: activeProjects, 
          icon: Briefcase, 
          color: 'text-blue-400', 
          bgColor: 'bg-blue-500/20' 
        },
        { 
          label: isAdmin ? 'Total Members' : 'Related Talent', 
          value: userPeople.length, 
          icon: Users, 
          color: 'text-green-400', 
          bgColor: 'bg-green-500/20' 
        },
        { 
          label: isAdmin ? 'Skills Mapped' : 'Project Skills', 
          value: isAdmin ? allSkills.length : uniqueSkills.size, 
          icon: Target, 
          color: 'text-purple-400', 
          bgColor: 'bg-purple-500/20' 
        },
        { 
          label: 'Completion Rate', 
          value: `${completionRate}%`, 
          icon: TrendingUp, 
          color: 'text-orange-400', 
          bgColor: 'bg-orange-500/20' 
        }
      ]);

      const recent = userProjects
        .slice(0, 3)
        .map(project => ({
          id: project.id,
          name: project.name,
          status: project.status,
          progress: project.progress || 0,
          teamSize: (project.teamMembers || []).length || (project.teamSize || 1),
          deadline: project.endDate || 'Active'
        }));
      setRecentProjects(recent);

      // Compute actual skill gaps from user's projects vs assigned talent
      const calculatedGaps = [];
      userProjects.forEach(proj => {
        const reqs = proj.requiredSkills || [];
        reqs.forEach(req => {
          const reqName = typeof req === 'string' ? req : req?.name;
          if (!reqName) return;
          const matchingMembers = userPeople.filter(p =>
            (p.skills || []).some(s => (typeof s === 'string' ? s : s?.name || '').toLowerCase() === reqName.toLowerCase())
          );
          if (matchingMembers.length === 0) {
            const reqLevel = typeof req === 'object' && req.level ? req.level : 'Intermediate';
            const reqImportance = typeof req === 'object' && req.importance ? req.importance : 'High';
            const reqHours = typeof req === 'object' && req.hours ? req.hours : (proj.estimatedHours || null);
            calculatedGaps.push({
              skill: reqName,
              gap: 1,
              project: proj.name,
              importance: reqImportance,
              level: reqLevel,
              hours: reqHours
            });
          }
        });
      });
      setSkillGaps(calculatedGaps.slice(0, 4));

      // Compute actual recent activities based on projects
      const acts = userProjects.slice(0, 3).map((proj, idx) => ({
        id: proj.id || idx + 1,
        type: 'project_created',
        message: `Project "${proj.name}" active (${proj.status || 'Planning'})`,
        time: proj.createdAt ? new Date(proj.createdAt).toLocaleDateString() : 'Active',
        user: proj.creator || currentUser?.name || 'You'
      }));
      setRecentActivity(acts);
    };

    updateDashboard();

    const handleStorageChange = () => updateDashboard();
    window.addEventListener('storage', handleStorageChange);
    
    const handleVisibilityChange = () => {
      if (!document.hidden) updateDashboard();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentUser, isAdmin]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return isDarkMode ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-green-50 text-green-800 border border-green-300 font-semibold';
      case 'In Progress': return isDarkMode ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : 'bg-blue-50 text-blue-800 border border-blue-300 font-semibold';
      case 'Planning': return isDarkMode ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' : 'bg-amber-100 text-amber-950 border border-amber-300 font-semibold';
      case 'On Hold': return isDarkMode ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-red-50 text-red-800 border border-red-300 font-semibold';
      default: return isDarkMode ? 'bg-gray-500/20 text-gray-400' : 'bg-gray-100 text-gray-700';
    }
  };

  const getProgressColor = (progress) => {
    if (progress >= 80) return 'bg-green-500';
    if (progress >= 60) return 'bg-blue-500';
    if (progress >= 40) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'project_created': return <Plus className="h-4 w-4 text-blue-400" />;
      case 'person_assigned': return <Users className="h-4 w-4 text-green-400" />;
      case 'skill_added': return <Target className="h-4 w-4 text-purple-400" />;
      case 'project_completed': return <CheckCircle className="h-4 w-4 text-green-400" />;
      default: return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  return (
    <div className={`space-y-6 transition-colors duration-200 ${
      isDarkMode ? 'text-white' : 'text-light-950'
    }`}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center">
            <h1 className="text-3xl font-bold">Dashboard</h1>
            {isAdmin ? (
              <span className="ml-3 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-500/20 text-purple-400 border border-purple-500/30">
                Admin Console
              </span>
            ) : (
              <span className="ml-3 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-500/20 text-blue-400 border border-blue-500/30">
                Participant Workspace
              </span>
            )}
          </div>
          <p className={`mt-1 transition-colors duration-200 ${
            isDarkMode ? 'text-dark-300' : 'text-light-600'
          }`}>
            {isAdmin 
              ? 'Organization-wide health, talent allocations, and cluster demands.' 
              : 'Overview of your workspace, skills, and open project opportunities.'}
          </p>
        </div>

        <div className="flex space-x-3">
          <Link
            to="/projects"
            className="btn-primary flex items-center text-sm"
          >
            <Briefcase className="h-4 w-4 mr-2" />
            Explore Projects & Apply
          </Link>
        </div>
      </div>

      {/* Profile Onboarding Callout Banner if incomplete */}
      {(!currentUser?.skills || currentUser?.skills.length === 0) && (
        <div className={`p-6 rounded-2xl border transition-all shadow-lg flex flex-col md:flex-row items-start md:items-center justify-between gap-6 ${
          isDarkMode
            ? 'bg-gradient-to-r from-primary-950/60 via-dark-900 to-dark-800 border-primary-500/30'
            : 'bg-gradient-to-r from-primary-50 via-white to-light-100 border-primary-200'
        }`}>
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-primary flex items-center justify-center text-white shadow-md flex-shrink-0">
              <Sparkles className="h-6 w-6" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h3 className="text-lg font-bold">Welcome, {currentUser?.name || 'Builder'}! Set Up Your Profile</h3>
                <span className="text-[11px] bg-primary-500/20 text-primary-400 font-bold px-2 py-0.5 rounded-full border border-primary-500/30">
                  Recommended First Step
                </span>
              </div>
              <p className={`text-sm mt-1 max-w-xl ${isDarkMode ? 'text-dark-300' : 'text-light-600'}`}>
                Add your skills, certifications, photo, and bio. Projects on SkillMesh match teammates automatically based on these skills!
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-3 w-full md:w-auto">
            <button
              onClick={openOnboarding}
              className="btn-primary py-2.5 px-5 text-sm flex items-center justify-center space-x-2 shadow-md w-full md:w-auto"
            >
              <Sparkles className="h-4 w-4" />
              <span>Launch Profile Setup</span>
            </button>
            <Link
              to="/me"
              className={`btn-secondary py-2.5 px-4 text-sm flex items-center justify-center space-x-1.5 ${isDarkMode ? 'dark' : 'light'}`}
            >
              <User className="h-4 w-4" />
              <span>Go to Profile</span>
            </Link>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <div key={index} className={`card transition-all duration-200 ${
            isDarkMode ? 'dark' : 'light'
          }`}>
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                <stat.icon className={`h-6 w-6 ${stat.color}`} />
              </div>
              <div>
                <div className="text-2xl font-bold">{stat.value}</div>
                <span className={`text-sm transition-colors duration-200 ${
                  isDarkMode ? 'text-dark-300' : 'text-light-600'
                }`}>
                  {stat.label}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Skills Gap Analysis & Recent Activity */}
        <div className="lg:col-span-2 space-y-6">
          {/* Skills Gap Analysis */}
          <div className={`card transition-all duration-200 ${
            isDarkMode ? 'dark' : 'light'
          }`}>
            <h2 className="text-xl font-semibold mb-4">Skills Demand & Gaps</h2>
            <div className="space-y-3">
              {skillGaps.length === 0 ? (
                <div className={`p-6 rounded-lg text-center ${
                  isDarkMode ? 'bg-dark-800 text-dark-300' : 'bg-light-100 text-light-600'
                }`}>
                  <CheckCircle className="h-8 w-8 text-green-400 mx-auto mb-2 opacity-80" />
                  <div className="font-medium text-sm">No skill gaps detected in your active projects.</div>
                  <div className="text-xs text-gray-500 mt-1">All required project skills are currently covered.</div>
                </div>
              ) : (
                skillGaps.map((gap, index) => (
                  <div key={index} className={`p-4 rounded-lg transition-colors duration-200 ${
                    isDarkMode ? 'bg-dark-800' : 'bg-light-100'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          gap.importance === 'Critical' ? (isDarkMode ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-red-50 text-red-800 border border-red-200') :
                          gap.importance === 'High' ? (isDarkMode ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30' : 'bg-orange-50 text-orange-800 border border-orange-200') :
                          gap.importance === 'Medium' ? (isDarkMode ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' : 'bg-amber-100 text-amber-950 border border-amber-300 font-bold') :
                          (isDarkMode ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : 'bg-blue-50 text-blue-800 border border-blue-200')
                        }`}>
                          {gap.importance}
                        </span>
                        <span className="font-medium">{gap.skill}</span>
                      </div>
                      <span className="text-sm font-medium text-red-400">
                        {gap.gap} needed
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm text-gray-400">
                      <span>{gap.project}</span>
                      <span>Level: {gap.level || 'Intermediate'}{gap.hours ? ` • ${gap.hours}h` : ''}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="mt-4 pt-4 border-t border-gray-700">
              <Link to={isAdmin ? "/graph" : "/projects"} className="text-primary-400 hover:text-primary-300 text-sm font-medium">
                {isAdmin ? "Explore Skill Clusters in Graph →" : "View My Projects & Skill Needs →"}
              </Link>
            </div>
          </div>

          {/* Recent Activity */}
          <div className={`card transition-all duration-200 ${
            isDarkMode ? 'dark' : 'light'
          }`}>
            <h2 className="text-xl font-semibold mb-4">Recent Activity</h2>
            <div className="space-y-4">
              {recentActivity.length === 0 ? (
                <div className={`p-4 rounded-lg text-center text-xs ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  No recent activity recorded. Publish a project to begin tracking team milestones.
                </div>
              ) : (
                recentActivity.map((activity) => (
                  <div key={activity.id} className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-1">
                      {getActivityIcon(activity.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{activity.message}</p>
                      <div className="flex items-center space-x-2 text-xs text-gray-400 mt-1">
                        <span>{activity.user}</span>
                        <span>•</span>
                        <span>{activity.time}</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Quick Actions & Recent Projects */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <div className={`card transition-all duration-200 ${
            isDarkMode ? 'dark' : 'light'
          }`}>
            <h2 className="text-xl font-semibold mb-4">Quick Actions</h2>
            <div className="space-y-3">
              <Link
                to="/projects/new"
                className="btn-primary w-full flex items-center justify-center"
              >
                <Plus className="h-5 w-5 mr-2" />
                Publish Project
              </Link>
              <Link
                to="/people"
                className="btn-secondary w-full flex items-center justify-center"
              >
                <Users className="h-5 w-5 mr-2" />
                Find Related Talent
              </Link>
              <Link
                to="/match"
                className="btn-secondary w-full flex items-center justify-center"
              >
                <Target className="h-5 w-5 mr-2" />
                Match Candidates
              </Link>
            </div>
          </div>

          {/* Recent Projects */}
          <div className={`card transition-all duration-200 ${
            isDarkMode ? 'dark' : 'light'
          }`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">
                {isAdmin ? 'Recent Projects' : 'My Projects'}
              </h2>
              <Link to="/projects" className="text-primary-400 hover:text-primary-300 text-sm">
                View All
              </Link>
            </div>
            
            {recentProjects.length > 0 ? (
              <div className="space-y-4">
                {recentProjects.map((project) => (
                  <div key={project.id} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium text-sm">{project.name}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
                        {project.status}
                      </span>
                    </div>
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs text-gray-400">
                        <span>Progress</span>
                        <span>{project.progress}%</span>
                      </div>
                      <div className="w-full bg-dark-800 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(project.progress)}`}
                          style={{ width: `${project.progress}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-sm text-dark-300 mb-3">No published projects yet.</p>
                <Link to="/projects/new" className="btn-primary text-xs py-1.5 px-3">
                  Publish Your First Project
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
